import { IconLoader2, IconXboxXFilled } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { Article, Category, Guest } from "../types";
import Link from "next/link";

interface ISearchResults {
  articles?: (Article & { url: string })[];
  guests?: (Guest & { url: string })[];
  categories?: (Category & { url: string })[];
}

export function Search() {
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchString, setSearchString] = useState("");

  const [isSearchResultsVisible, toggleSearchResultsVisible] = useState(false);
  const [searchResults, setSearchResults] = useState<ISearchResults>({});
  const [selectedResult, setSelectedResult] = useState(-1);

  const [isSearching, setIsSearching] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isSearchVisible && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSearchVisible]);

  useEffect(() => {
    if (searchString.trim().length < 3) {
      setSearchResults({});
      toggleSearchResultsVisible(false);
      setIsSearching(false);
      setSelectedResult(-1);
      return;
    }
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    if (isSearchVisible) {
      setIsSearching(true);

      debounceTimeout.current = setTimeout(() => {
        handleSearch(searchString);
      }, 300);
    }

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [searchString, isSearchVisible]);

  useEffect(() => {
    if (!isSearchResultsVisible) return;

    function handleClickOutside(event: MouseEvent) {
      if (inputRef.current && inputRef.current.contains(event.target as Node)) {
        return;
      }
      if (
        searchResultsRef.current &&
        !searchResultsRef.current.contains(event.target as Node)
      ) {
        handleSearchResultsClick();
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (!isSearchResultsVisible) return;

      const totalResults =
        (searchResults.articles?.length || 0) +
        (searchResults.guests?.length || 0) +
        (searchResults.categories?.length || 0);

      if (totalResults === 0) return;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setSelectedResult((prev) => (prev + 1) % totalResults);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setSelectedResult((prev) => (prev - 1 + totalResults) % totalResults);
      } else if (event.key === "Enter" && selectedResult >= 0) {
        event.preventDefault();
        const allResults = [
          ...(searchResults.articles || []),
          ...(searchResults.guests || []),
          ...(searchResults.categories || []),
        ];
        if (allResults[selectedResult]) {
          window.location.href = allResults[selectedResult].url;
          handleSearchResultsClick();
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSearchResultsVisible, searchResults, selectedResult]);

  async function handleSearch(value: string) {
    setIsSearching(true);
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:1337";

    const [articles, guests, categories] = await Promise.all([
      fetch(
        new URL(
          `/api/articles?filters[$or][0][searchString][$containsi]=${value}&filters[$or][1][title][$containsi]=${value}&filters[$or][2][content][$containsi]=${value}`,
          apiUrl
        )
      ).then((res) => res.json()),
      fetch(
        new URL(
          `/api/guests?filters[$or][0][name][$containsi]=${value}`,
          apiUrl
        )
      ).then((res) => res.json()),
      fetch(
        new URL(
          `/api/categories?filters[$or][0][name][$containsi]=${value}&filters[$or][1][description][$containsi]=${value}`,
          apiUrl
        )
      ).then((res) => res.json()),
    ]);
    if (articles.data.length || guests.data.length || categories.data.length) {
      setSearchResults({
        articles: articles.data.map((d) => ({
          ...d,
          url: `/podcast/${d.slug}`,
        })),
        guests: guests.data.map((d) => ({ ...d, url: `/guests/${d.slug}` })),
        categories: categories.data.map((d) => ({
          ...d,
          url: `/categories/${d.slug}`,
        })),
      });
    } else {
      setSearchResults({});
    }
    setIsSearching(false);
    toggleSearchResultsVisible(true);
  }

  function handleSearchResultsClick() {
    toggleSearchResultsVisible(false);
    setIsSearchVisible(false);
    setSelectedResult(-1);
    // setSearchString("");
  }

  return (
    <div>
      {!isSearchVisible && (
        <p className="cursor-pointer" onClick={() => setIsSearchVisible(true)}>
          Search
        </p>
      )}
      {isSearchVisible && (
        <div className="relative flex items-center  w-full">
          {isSearching ? (
            <IconLoader2 className="absolute left-0 top-[2px] w-[18px] h-[18px] animate-spin" />
          ) : (
            <IconXboxXFilled
              className="absolute left-0 top-[2px] w-[18px] h-[18px]"
              onClick={() => setIsSearchVisible(false)}
            />
          )}

          <input
            type="text"
            ref={inputRef}
            placeholder="Search"
            className="bg-transparent outline-none pl-6 w-full"
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
          />
          {isSearchResultsVisible && (
            <div
              ref={searchResultsRef}
              className="absolute left-0 z-20 w-[330px] top-[40px] lg:top-[42px] search-results w-100 rounded-2xl rounded-t-none shadowed"
              style={{ backgroundColor: "#fafafa" }}
            >
              <ul>
                {searchResults.articles?.length ||
                searchResults.guests?.length ||
                searchResults.categories?.length ? (
                  <>
                    {(searchResults.articles || []).map((result, index) => (
                      <li
                        key={index}
                        className={` hover:bg-black hover:text-white ${
                          selectedResult === index ? "bg-black text-white" : ""
                        }`}
                      >
                        <Link
                          href={result.url}
                          className="border-t border-black block p-2"
                          onClick={handleSearchResultsClick}
                        >
                          {result.title}
                        </Link>
                      </li>
                    ))}
                    {(searchResults.guests || []).map((result, index) => (
                      <li
                        key={index}
                        className={` hover:bg-black hover:text-white ${
                          selectedResult ===
                          index + (searchResults.articles?.length || 0)
                            ? "bg-black text-white"
                            : ""
                        }`}
                      >
                        <Link
                          href={result.url}
                          className="border-t border-black block p-2"
                          onClick={handleSearchResultsClick}
                        >
                          {result.name}
                        </Link>
                      </li>
                    ))}
                    {(searchResults.categories || []).map((result, index) => (
                      <li
                        key={index}
                        className={`border-t border-black hover:bg-black hover:text-white ${
                          selectedResult ===
                          index +
                            (searchResults.articles?.length || 0) +
                            (searchResults.guests?.length || 0)
                            ? "bg-black text-white"
                            : ""
                        }`}
                      >
                        <Link
                          href={result.url}
                          className="border-t border-black block p-2"
                          onClick={handleSearchResultsClick}
                        >
                          {result.name}
                        </Link>
                      </li>
                    ))}
                  </>
                ) : (
                  <li key="search-not-found" className="border-b-1">
                    <p className="block p-2 cursor-default">No results found</p>
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
